import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { AlertTriangle, Plus, RefreshCw, Sliders } from 'lucide-react';

interface BranchItem {
  id: string;
  name: string;
  company?: { name: string };
}

interface UserProfile {
  id: string;
  name: string;
  rateLimit: string | null;
}

type ModalMode = 'create' | 'edit';

const rateLimitRegex = /^\d+(K|M)$/i;

const parseRateLimit = (value: string | null | undefined) => {
  if (!value) return null;
  const [rx, tx] = value.split('/');
  if (!rx || !tx) return null;
  if (!rateLimitRegex.test(rx) || !rateLimitRegex.test(tx)) return null;
  return { rx: rx.toUpperCase(), tx: tx.toUpperCase() };
};

const HotspotProfiles: React.FC = () => {
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingProfile, setEditingProfile] = useState<UserProfile | null>(null);
  const [formValues, setFormValues] = useState({
    name: '',
    rx: '',
    tx: ''
  });
  const [rateLimitRaw, setRateLimitRaw] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedBranchLabel = useMemo(() => {
    const branch = branches.find((b) => b.id === selectedVesselId);
    if (!branch) return 'Selecione um vessel';
    return branch.company?.name ? `${branch.name} - ${branch.company.name}` : branch.name;
  }, [branches, selectedVesselId]);

  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get('/branches');
      const data = res.data || [];
      setBranches(data);
      if (!selectedVesselId && data.length > 0) {
        setSelectedVesselId(data[0].id);
      }
    } catch (err) {
      toast.error('Falha ao carregar vessels');
    }
  }, [selectedVesselId]);

  const loadProfiles = useCallback(async (vesselId: string) => {
    if (!vesselId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/vessels/${vesselId}/mikrotik/hotspot/user-profiles`);
      setProfiles(res.data || []);
    } catch (err) {
      setError('Falha ao carregar perfis do hotspot.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (selectedVesselId) {
      loadProfiles(selectedVesselId);
    }
  }, [loadProfiles, selectedVesselId]);

  const openCreate = () => {
    setModalMode('create');
    setEditingProfile(null);
    setRateLimitRaw(null);
    setFormValues({ name: '', rx: '', tx: '' });
    setModalOpen(true);
  };

  const openEdit = (profile: UserProfile) => {
    setModalMode('edit');
    setEditingProfile(profile);
    const parsed = parseRateLimit(profile.rateLimit || '');
    setRateLimitRaw(profile.rateLimit || null);
    setFormValues({
      name: profile.name,
      rx: parsed?.rx || '',
      tx: parsed?.tx || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const validateRateLimitField = (value: string) => rateLimitRegex.test(value);

  const validateForm = () => {
    if (modalMode === 'create' && formValues.name.trim().length === 0) {
      toast.error('Nome do perfil é obrigatório.');
      return false;
    }
    if (!validateRateLimitField(formValues.rx)) {
      toast.error('RX inválido. Use apenas números com K ou M (ex.: 512K, 2M).');
      return false;
    }
    if (!validateRateLimitField(formValues.tx)) {
      toast.error('TX inválido. Use apenas números com K ou M (ex.: 512K, 2M).');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVesselId || !validateForm()) return;
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await api.post(`/vessels/${selectedVesselId}/mikrotik/hotspot/user-profiles`, {
          name: formValues.name.trim(),
          rx: formValues.rx.toUpperCase(),
          tx: formValues.tx.toUpperCase()
        });
        toast.success('Perfil criado com sucesso.');
      } else if (editingProfile) {
        await api.put(
          `/vessels/${selectedVesselId}/mikrotik/hotspot/user-profiles/${editingProfile.id}`,
          {
            rx: formValues.rx.toUpperCase(),
            tx: formValues.tx.toUpperCase()
          }
        );
        toast.success('Perfil atualizado com sucesso.');
      }
      setModalOpen(false);
      await loadProfiles(selectedVesselId);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        (modalMode === 'create' ? 'Falha ao criar perfil.' : 'Falha ao atualizar perfil.');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const retry = () => {
    if (selectedVesselId) {
      loadProfiles(selectedVesselId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Perfis do Hotspot (por Vessel)</h1>
          <p className="text-sm text-gray-500">Gerencie profiles do hotspot para o vessel selecionado.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => loadProfiles(selectedVesselId)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            disabled={loading || !selectedVesselId}
          >
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button
            onClick={openCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            disabled={!selectedVesselId}
          >
            <Plus size={16} />
            Novo Perfil
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Vessel</label>
        <select
          value={selectedVesselId}
          onChange={(e) => setSelectedVesselId(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {branches.length === 0 && <option value="">Nenhum vessel disponível</option>}
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name} {branch.company?.name ? `- ${branch.company.name}` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">{selectedBranchLabel}</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-10 text-center text-gray-600 space-y-3">
            <AlertTriangle className="mx-auto text-yellow-500" />
            <p>{error}</p>
            <button
              onClick={retry}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        ) : profiles.length === 0 ? (
          <div className="p-12 text-center text-gray-500 space-y-2">
            <Sliders className="mx-auto text-gray-400" />
            <p>Sem perfis cadastrados neste vessel.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nome do Perfil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Rate Limit
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {profiles.map((profile) => (
                  <tr key={profile.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {profile.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {profile.rateLimit ? profile.rateLimit : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEdit(profile)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {modalMode === 'create' ? 'Novo Perfil' : 'Editar Perfil'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {modalMode === 'create' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Perfil</label>
                  <input
                    type="text"
                    value={formValues.name}
                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="profile_hotspot"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Perfil</label>
                  <input
                    type="text"
                    value={formValues.name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Download (RX)</label>
                  <input
                    type="text"
                    value={formValues.rx}
                    onChange={(e) => setFormValues({ ...formValues, rx: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="2M"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload (TX)</label>
                  <input
                    type="text"
                    value={formValues.tx}
                    onChange={(e) => setFormValues({ ...formValues, tx: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="10M"
                  />
                </div>
              </div>

              {modalMode === 'edit' && rateLimitRaw && !parseRateLimit(rateLimitRaw) && (
                <div className="text-xs text-amber-600">
                  Rate limit atual inválido: {rateLimitRaw}. Corrija para salvar.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Queue</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                  HOTSPOT
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : modalMode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotspotProfiles;
